import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getCapabilityToken } from "@/lib/concierge/capability-token";
import { queryKeys } from "./queryKeys";

/**
 * Query options for fetching all active seller carts.
 */
export function sellerCartsQuery() {
  return queryOptions({
    queryKey: queryKeys.sellerCarts.all,
    queryFn: () => sdk.store.sellerCart.list(),
    staleTime: 0 // Cart data changes frequently
  });
}

/**
 * Fetches all active seller carts for the authenticated customer.
 *
 * In the multi-seller cart architecture, each customer can have
 * one active cart per seller. This hook returns all carts with
 * a summary of totals across all carts.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSellerCarts();
 *
 * // Show cart count in navbar
 * const itemCount = data?.summary.total_items ?? 0;
 *
 * // Render each seller's cart
 * data?.carts.map(cart => (
 *   <SellerCartGroup key={cart.cart_id} sellerCart={cart} />
 * ));
 * ```
 */
export function useSellerCarts(options?: {
  enabled?: boolean;
  /**
   * Optional filter: only return carts with these IDs
   * Used for filtering by selected carts in UI
   */
  cartIds?: string[]; // NEW: optional filter
}) {
  return useQuery({
    queryKey: options?.cartIds?.length
      ? queryKeys.sellerCarts.filtered(options.cartIds)
      : queryKeys.sellerCarts.all,
    queryFn: () =>
      sdk.store.sellerCart.list(
        options?.cartIds?.length ? { cart_ids: options.cartIds } : undefined
      ),
    staleTime: 0,
    enabled: options?.enabled ?? true
  });
}

/**
 * Query options for fetching a single cart by ID.
 */
export function sellerCartQuery(cartId: string) {
  return queryOptions({
    queryKey: queryKeys.sellerCarts.detail(cartId),
    queryFn: () => sdk.store.sellerCart.retrieve(cartId),
    enabled: !!cartId,
    staleTime: 0
  });
}

/**
 * Fetches a single cart by ID using standard Medusa cart endpoint.
 * Use for cart operations that need full cart data.
 *
 * @example
 * ```tsx
 * const { data } = useSellerCart(cartId);
 * console.log(data?.cart.items);
 * ```
 */
export function useSellerCart(cartId: string, options?: { enabled?: boolean }) {
  return useQuery({
    ...sellerCartQuery(cartId),
    enabled: !!cartId && (options?.enabled ?? true)
  });
}

/**
 * Query options for fetching shipping options for a cart.
 */
export function cartShippingOptionsQuery(cartId: string) {
  return queryOptions({
    queryKey: queryKeys.sellerCarts.shippingOptions(cartId),
    queryFn: () => sdk.store.fulfillment.listCartOptions({ cart_id: cartId }),
    enabled: !!cartId
  });
}

/**
 * Fetches shipping options for a specific cart.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useCartShippingOptions(cartId);
 *
 * data?.shipping_options.map(option => (
 *   <ShippingOption key={option.id} option={option} />
 * ));
 * ```
 */
export function useCartShippingOptions(
  cartId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    ...cartShippingOptionsQuery(cartId),
    enabled: !!cartId && (options?.enabled ?? true)
  });
}

/**
 * Query options for the variants a cart line can be switched to.
 *
 * `gcTime: 0` and `staleTime: 0`: which variant is current changes with every
 * switch (and the line id itself can change when a switch merges into another
 * line), so a cached list from an earlier open would flash the wrong "current"
 * marker before the refetch landed.
 */
export function lineItemVariantOptionsQuery(
  cartId: string,
  lineItemId: string
) {
  return queryOptions({
    queryKey: queryKeys.sellerCarts.variantOptions(cartId, lineItemId),
    queryFn: () =>
      sdk.store.sellerCart.getLineItemVariantOptions(cartId, lineItemId),
    staleTime: 0,
    gcTime: 0
  });
}

/**
 * Fetches the variants a cart line can be switched to. Lazy by design: pass
 * `enabled` only once the picker is opened, so a cart of forty lines does not
 * make forty requests on load.
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError } = useLineItemVariantOptions(
 *   cartId,
 *   item.id,
 *   { enabled: isOpen }
 * );
 * ```
 */
export function useLineItemVariantOptions(
  cartId: string,
  lineItemId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    ...lineItemVariantOptionsQuery(cartId, lineItemId),
    enabled: !!cartId && !!lineItemId && (options?.enabled ?? true)
  });
}

/**
 * Query key prefix for the ad hoc seller-suggestion lookup (KAN-193).
 *
 * Deliberately NOT nested under `queryKeys.sellerCarts` even though it lives
 * in the same `sellerCart` SDK resource: `invalidateSellerCarts`
 * (`./cache.ts`) invalidates every query whose key starts with the
 * `seller-carts` prefix (barring the shipping-options segment it explicitly
 * carves out), and this lookup has nothing to do with cart *contents* - it's
 * a suggestion keyed on the captured page's hosts, not on any cart. Sharing
 * the prefix would mean an unrelated cart mutation elsewhere (e.g. a quantity
 * edit on a different seller's cart) silently refetches this on every settle.
 */
const AD_HOC_SELLER_SUGGESTION_KEY = "ad-hoc-seller-suggestion" as const;

/**
 * Suggests a seller for the ad hoc add form's always-present dropdown
 * (KAN-193), by normalized host match against the page Caroline captured
 * from. `enabled` only once the captured hosts are known - the paste-in
 * form's `source_hosts` come from parsing the pasted clipboard JSON, so there
 * is nothing to suggest against before that succeeds.
 *
 * Also disabled outright when no capability token is stored: the same token
 * that authorizes the add call authorizes this lookup, so without one the
 * request cannot succeed and there is nothing to fetch (mirrors {@link
 * useAddAdHocSellerCartItem}'s fail-fast reasoning).
 *
 * @example
 * ```tsx
 * const { data } = useAdHocSellerSuggestion(capture?.source_hosts, {
 *   enabled: Boolean(capture)
 * });
 * const sellers = data?.sellers ?? [];
 * const suggestedSellerId = data?.suggested_seller_id ?? null;
 * ```
 */
export function useAdHocSellerSuggestion(
  hosts: SalonStockerHttpTypes.StoreSuggestAdHocSellerQuery | undefined,
  options?: { enabled?: boolean }
) {
  const token = getCapabilityToken();

  return useQuery({
    queryKey: [
      AD_HOC_SELLER_SUGGESTION_KEY,
      hosts?.browsing_host,
      hosts?.canonical_myshopify_host
    ] as const,
    queryFn: () =>
      // Non-null assertions are safe here: `enabled` below gates the query on
      // both being present before this ever runs.
      sdk.store.sellerCart.suggestAdHocSellers(hosts!, {
        "x-concierge-capability-token": token!
      }),
    enabled: Boolean(hosts) && Boolean(token) && (options?.enabled ?? true)
  });
}
